import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { addSlidesToAnnotationAssignment, getAllAnnotationAssignments } from 'api/annotationAssignments';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import { ActionDialogType, useActiveActionDialogType, useAdditionalParamsDialog } from 'components/ActionDialog';
import { modelTypeArea, modelTypeCell, modelTypeDefect } from 'components/Pages/Jobs/inferenceFieldsOptions';
import useMainFilters from 'components/SearchFilters/hooks/useMainFilters';
import {
  getDefaultManifestDialogUrlState,
  ManifestSurfaceParam,
  PendingSlidesSelectionAction,
} from 'components/StudyDashboard/CaseManifest/dialogState';
import { buildOrganizationManifestState } from 'components/StudyDashboard/CaseManifest/organizationEntry';
import { getDatasetMode } from 'components/StudyDashboard/ProceduresPage/datasetMode';
import { isRnaOnlyStudyWorkflow } from 'components/StudyDashboard/Workflow/studyWorkflowConfiguration';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { PipRunNameEnum } from 'interfaces/jobs/pipsRunNames';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { filter, includes, isEmpty, map, orderBy, uniqBy } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { BooleanParam, DelimitedArrayParam, JsonParam, StringParam, useQueryParam } from 'use-query-params';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import queryClient from 'utils/queryClient';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { useCasesParams } from 'utils/useCasesParams';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import { useStudyAvailableAlgorithms } from 'utils/useStudyAvailableAlgorithms';

export interface ActionMenuItemOption {
  type?: ActionDialogType;
  header: string;
  hide?: boolean; // don't show option at all
  disabled?: boolean; // show option but disabled
  customMenuItem?: React.ReactNode;
  subMenuItems?: ActionMenuItemOption[];
  onClick?: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  isListSubheader?: boolean;
}

interface InferenceActionDialogParams {
  inferenceDialogHeader?: string;
  inferencePresetModelType?: string;
}

const assignmentsLoadingSubItems: ActionMenuItemOption[] = [
  {
    type: ActionDialogType.AddToAssignment,
    header: 'Loading annotation assignments...',
    disabled: true,
  },
];

const assignmentsErrorSubItems: ActionMenuItemOption[] = [
  {
    type: ActionDialogType.AddToAssignment,
    header: 'Error loading annotation assignments',
    disabled: true,
  },
];

export const useActionMenuItemOptions = ({ currentStains }: { currentStains?: string[] }) => {
  const { labId } = useCurrentLabId();
  const [filters] = useQueryParam('filters', JsonParam);
  const [, setIsManifestDialogOpen] = useQueryParam('isManifestDialogOpen', BooleanParam);
  const [, setSlidesMode] = useQueryParam('slidesMode', BooleanParam);
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);
  const [, setCaseManifestSelectionMode] = useQueryParam('caseManifestSelectionMode', StringParam);
  const [, setCaseManifestSelectedRows] = useQueryParam('caseManifestSelectedRows', DelimitedArrayParam);
  const [, setCaseManifestOmittedRows] = useQueryParam('caseManifestOmittedRows', DelimitedArrayParam);
  const [, setManifestDialogUrlState] = useQueryParam('caseManifestState', CompressedJsonParam);
  const [, setManifestSurface] = useQueryParam('manifestSurface', ManifestSurfaceParam);
  const { isStudyIdSpecific } = useMainFilters();
  const studyId = filters?.studyId;
  const studyIdIsSpecific = isStudyIdSpecific(studyId);
  const { selectionMode, selectedRows, omittedRows } = useRowSelectionContext();
  const hasSingleExplicitlySelectedRow = selectionMode === 'select' && selectedRows.length === 1;

  const { data: availableAlgorithms, isLoading: isLoadingStudyAlgorithms } = useStudyAvailableAlgorithms(studyId);
  const { study, isLoading: isLoadingStudy } = useStudy(studyId, { enabled: studyIdIsSpecific });
  const isRnaOnlyStudySetup = isRnaOnlyStudyWorkflow(study?.settings?.workflow?.modules);
  const hasStudyAlgorithms = !isEmpty(availableAlgorithms);

  const {
    queryParams: { slidesMode, page, searchTerm },
  } = useEncodedFilters();

  const { hasPermission } = usePermissions();
  const canApproveResults = hasPermission(Permission.PublishResults);
  const canApproveCases = hasPermission(Permission.ApproveCases);
  const canRunBrightfieldTissueSegmentationMask = hasPermission(Permission.ExecuteBrightfieldTissueSegmentationMask);
  const canAssignNeighborhoodNames = hasPermission(Permission.AssignGenericClusters) && studyIdIsSpecific;
  const canRunInference = hasPermission(Permission.RunInference);
  const canRunCalculateFeatures = hasPermission(Permission.RunCalculateFeatures);
  const canRunSlideRegistrations = hasPermission(Permission.ExecuteSlideRegistrations);
  const canRunMultiplexCellSegmentation = hasPermission(Permission.RunMultiplexCellSegmentation);
  const canRunMultiplexBinaryClassifier = hasPermission(Permission.ExecuteMultiplexBinaryClassifier);
  const canRunMultiplexTissueSegmentationMask = hasPermission(Permission.ExecuteMultiplexTissueSegmentationMask);
  const canRunManualPostIngestPipeline = hasPermission(Permission.ExecuteManualPostIngestPipeline);
  const canRunMultiplexNormalization = hasPermission(Permission.ExecuteMultiplexNormalization);
  const canRunMultiplexHistogram = hasPermission(Permission.ExecuteMultiplexHistogram);
  const canRunMultiplexThreshold = hasPermission(Permission.ExecuteMultiplexThreshold);
  const canRunMultiplexCellTyping = hasPermission(Permission.ExecuteMultiplexCellTyping);
  const canRunMultiplexBlobSegmentation = hasPermission(Permission.ExecuteMultiplexBlobSegmentation);
  const canRunMultiplexPeakDetection = hasPermission(Permission.ExecuteMultiplexPeakDetection);
  const canRunNeighborhoodClusters = hasPermission(Permission.ExecuteNeighborhoodClusters);
  const canExecuteCloudOptimizedSlides = hasPermission(Permission.ExecuteCloudOptimizedSlides);
  const canExecuteTileGeneration = hasPermission(Permission.ExecuteTileGeneration);
  const canCreateAnnotationAssignment = hasPermission(Permission.CreateAnnotationAssignment);
  const canRunCropTma = hasPermission(Permission.RunCropTma);
  const canExecuteStudyAlgorithms = hasPermission(Permission.ExecuteStudyAlgorithms);
  const canRunCellSegmentation = hasPermission(Permission.ExecuteCellSegmentation);
  const canRunQuantification = hasPermission(Permission.ExecuteQuantification);
  const canRunQaAnalysis = hasPermission(Permission.ExecuteQaAnalysis);
  const canRunQcAnalysis = hasPermission(Permission.ExecuteQcAnalysis);
  const canRunMifMarkerUnsupervisedAnalysis = hasPermission(Permission.ExecuteMifMarkerUnsupervisedAnalysis);
  const canExecuteStudyReport = hasPermission(Permission.ExecuteStudyReport);
  const canEditSlideTagAssignments = hasPermission(Permission.EditSlideTagsAssignments);
  const canEditQCMarkers = hasPermission(Permission.EditQCMarkers);

  const caseParamsForStudy = { labId, filters: { studyId } };
  const { data: stains, isLoading: isLoadingStains } = useQuery({
    queryKey: ['slidesStainsTypes', caseParamsForStudy],
    queryFn: ({ signal }) => getStainTypeFilteredIds(caseParamsForStudy, signal),
    placeholderData: currentStains,
  });

  const { casesParams } = useCasesParams();

  const isMultiplex = includes(stains, MULTIPLEX_STAIN_ID);

  const isBrightfield = !isEmpty(filter(stains, (stain) => stain !== MULTIPLEX_STAIN_ID));

  const {
    data: studyAnnotationAssignmentResponse,
    isLoading: isLoadingStudyAssignments,
    isError: isErrorStudyAssignments,
    queryKey: annotationAssignmentsQueryKey,
  } = useAnnotationAssignments(studyId);

  const {
    data: globalAnnotationAssignments,
    isLoading: isLoadingGlobalAssignments,
    isError: isErrorGlobalAssignments,
  } = useQuery({
    queryKey: ['globalAnnotationAssignments'],
    queryFn: ({ signal }: QueryFunctionContext) => getAllAnnotationAssignments({ isGlobal: true, signal }),
    enabled: canCreateAnnotationAssignment,
  });

  const studyAnnotationAssignments = studyAnnotationAssignmentResponse?.annotationAssignments;
  const annotationAssignments = useMemo(() => {
    if (studyAnnotationAssignments && globalAnnotationAssignments) {
      return orderBy(
        uniqBy([...studyAnnotationAssignments, ...globalAnnotationAssignments], 'annotationAssignmentId'),
        'annotationAssignmentId',
        'desc'
      );
    }
    return studyAnnotationAssignments || globalAnnotationAssignments || [];
  }, [studyAnnotationAssignments, globalAnnotationAssignments]);

  const isLoadingAssignments = isLoadingStudyAssignments || isLoadingGlobalAssignments;
  const isErrorAssignments = isErrorStudyAssignments || isErrorGlobalAssignments;

  const addSlidesToAssignmentMutation = useMutationWithErrorSnackbar({
    mutationFn: addSlidesToAnnotationAssignment,
    mutationDescription: 'add slides to annotation assignment',
    onSuccess: (data: any) => {
      enqueueSnackbar(data?.message || 'Slides added to assignment', { variant: 'success' });
      queryClient.invalidateQueries(annotationAssignmentsQueryKey);
      queryClient.invalidateQueries(['annotations']);
    },
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
      setSubmenuAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const [, setCurrentActionItemType] = useActiveActionDialogType();
  const [, updateAdditionalParams] = useAdditionalParamsDialog();

  const openActionDialog = useCallback(
    (actionDialogType: ActionDialogType, additionalParams?: Record<string, any>) => {
      updateAdditionalParams(additionalParams);
      setCurrentActionItemType(actionDialogType);
      handleMenuClose();
      handleSubmenuClose();
    },
    [updateAdditionalParams, setCurrentActionItemType]
  );

  const openInferenceDialog = useCallback(
    (additionalParams: InferenceActionDialogParams) => {
      openActionDialog(ActionDialogType.Inference, additionalParams);
    },
    [openActionDialog]
  );

  const openPendingSlidesManifestDialog = useCallback(
    (pendingSlidesSelectionAction: PendingSlidesSelectionAction) => {
      setManifestDialogUrlState(
        buildOrganizationManifestState({
          manifestState: {
            ...getDefaultManifestDialogUrlState(),
            pendingSlidesSelectionAction,
          },
          returnTo: {
            origin: 'dataset',
            labId,
            studyId: String(studyId),
            datasetMode: getDatasetMode({ slidesMode, pendingSlidesMode }),
            page,
            searchTerm: searchTerm || undefined,
          },
        }),
        'replaceIn'
      );
      setCaseManifestSelectionMode(selectionMode, 'replaceIn');
      setCaseManifestSelectedRows(map(selectedRows, String), 'replaceIn');
      setCaseManifestOmittedRows(map(omittedRows, String), 'replaceIn');
      setManifestSurface('Manifest', 'replaceIn');
      setSlidesMode(true, 'replaceIn');
      setIsManifestDialogOpen(true, 'replaceIn');
      handleMenuClose();
      handleSubmenuClose();
    },
    [
      omittedRows,
      labId,
      page,
      pendingSlidesMode,
      searchTerm,
      selectedRows,
      selectionMode,
      slidesMode,
      studyId,
      setCaseManifestOmittedRows,
      setCaseManifestSelectedRows,
      setCaseManifestSelectionMode,
      setIsManifestDialogOpen,
      setManifestDialogUrlState,
      setManifestSurface,
      setSlidesMode,
    ]
  );

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSubmenuAnchorEl(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const addToAssignmentSubItems = useMemo(
    () =>
      isLoadingAssignments
        ? assignmentsLoadingSubItems
        : isErrorAssignments
        ? assignmentsErrorSubItems
        : map(orderBy(annotationAssignments, 'annotationAssignmentId', 'desc'), (assignment) => ({
            type: ActionDialogType.AddToAssignment,
            header: `${assignment.annotationAssignmentId}${assignment.isGlobal ? ' (Global)' : ''} ${assignment.name}-${
              assignment.createdAt
            }`,
            enabled: canCreateAnnotationAssignment,
            onClick: () => {
              setCurrentActionItemType(null);
              handleMenuClose();
              handleSubmenuClose();
              addSlidesToAssignmentMutation.mutate({
                annotationAssignmentId: assignment.annotationAssignmentId,
                casesParams,
              });
            },
          })),
    [isLoadingAssignments, isErrorAssignments, annotationAssignments, canCreateAnnotationAssignment, casesParams]
  );

  const actionItems: ActionMenuItemOption[] = useMemo(() => {
    return [
      {
        isListSubheader: true,
        header: 'Study Actions',
      },
      {
        type: ActionDialogType.ExecuteStudyAlgorithm,
        header: 'Run Study Algorithm',
        hide: !canExecuteStudyAlgorithms || !hasStudyAlgorithms,
      },
      {
        type: ActionDialogType.ApproveResults,
        header: 'Approve Results',
        hide: !canApproveResults,
      },
      {
        type: ActionDialogType.ApproveCases,
        header: 'Approve Cases',
        hide: !canApproveCases || slidesMode,
      },
      {
        type: ActionDialogType.DisapproveCases,
        header: 'Disapprove Cases',
        hide: !canApproveCases || slidesMode,
      },
      {
        type: ActionDialogType.CreateCohort,
        header: 'Create Cohort',
      },
      {
        header: hasSingleExplicitlySelectedRow ? 'Assign to a new case' : 'Group into one case',
        hide: !(slidesMode && pendingSlidesMode && studyIdIsSpecific),
        onClick: () => openPendingSlidesManifestDialog('groupSelectedSlidesIntoSingleCase'),
      },
      {
        header: 'Assign each slide to its own case',
        hide: !(slidesMode && pendingSlidesMode && studyIdIsSpecific) || hasSingleExplicitlySelectedRow,
        onClick: () => openPendingSlidesManifestDialog('assignSelectedSlidesToOwnCases'),
      },
      {
        isListSubheader: true,
        header: 'Annotation Assignments',
      },
      {
        type: ActionDialogType.CreateAssignment,
        header: 'Create Assignment',
        hide: !canCreateAnnotationAssignment,
      },
      {
        type: ActionDialogType.AddToAssignment,
        header: 'Add To Existing Assignment',
        hide: !canCreateAnnotationAssignment,
        subMenuItems: addToAssignmentSubItems,
      },
      {
        isListSubheader: true,
        header: 'Brightfield Pipeline',
        hide: !isBrightfield,
      },
      {
        type: ActionDialogType.BrightfieldTissueSegmentationMask,
        header: 'Run Tissue Segmentation Mask',
        hide: !(canRunBrightfieldTissueSegmentationMask && isBrightfield),
      },
      {
        type: ActionDialogType.CellSegmentation,
        header: 'Run Cell Segmentation',
        hide: !(canRunCellSegmentation && isBrightfield),
      },
      {
        header: 'Run Area Segmentation',
        hide: !(canRunInference && isBrightfield),
        onClick: () =>
          openInferenceDialog({
            inferenceDialogHeader: 'Run Area Segmentation',
            inferencePresetModelType: modelTypeArea.value,
          }),
      },
      {
        header: 'Run Defect Segmentation',
        hide: !(canRunInference && isBrightfield),
        onClick: () =>
          openInferenceDialog({
            inferenceDialogHeader: 'Run Defect Segmentation',
            inferencePresetModelType: modelTypeDefect.value,
          }),
      },
      {
        header: 'Run Cell Detection Inference',
        hide: !(canRunInference && isBrightfield),
        onClick: () =>
          openInferenceDialog({
            inferenceDialogHeader: 'Run Cell Detection Inference',
            inferencePresetModelType: modelTypeCell.value,
          }),
      },
      {
        isListSubheader: true,
        header: 'Multiplex Pipeline',
        hide: !isMultiplex,
      },
      {
        type: ActionDialogType.MultiplexTissueSegmentationMask,
        header: 'Run Tissue Segmentation Mask',
        hide: !(canRunMultiplexTissueSegmentationMask && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexHistogram,
        header: 'Run Histogram',
        hide: !(canRunMultiplexHistogram && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexNormalization,
        header: 'Run Normalization',
        hide: !(canRunMultiplexNormalization && isMultiplex),
      },
      {
        type: ActionDialogType.BulkMultiplexNormalization,
        header: 'Set Normalization Range',
        hide: !(canRunMultiplexNormalization && isMultiplex),
      },
      {
        type: ActionDialogType.BulkMultiplexMarkerNaming,
        header: 'Define multiplex panels',
        hide: !(canEditQCMarkers && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexCellSegmentation,
        header: 'Run Cell Segmentation',
        hide: !(canRunMultiplexCellSegmentation && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexThreshold,
        header: 'Run Threshold',
        hide: !(canRunMultiplexThreshold && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexBinaryClassifier,
        header: 'Run Binary Classifier',
        hide: !(canRunMultiplexBinaryClassifier && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexCellTyping,
        header: 'Fit KNN Model For Multiplex Cell Typing',
        hide: !(canRunMultiplexCellTyping && isMultiplex) || isRnaOnlyStudySetup,
      },
      {
        type: ActionDialogType.PostProcessingAndCalculateFeaturesSubMenu,
        header: 'Apply Cell Typing (Post Processing)',
        hide: !(canRunCalculateFeatures && isMultiplex) || isRnaOnlyStudySetup,
        onClick: () =>
          openActionDialog(ActionDialogType.PostProcessingAndCalculateFeaturesSubMenu, {
            pipRunName: PipRunNameEnum.CellTyping,
          }),
      },
      {
        type: ActionDialogType.PostProcessingAndCalculateFeaturesSubMenu,
        header: 'Apply Neighborhoods (Post Processing)',
        hide: !(canRunCalculateFeatures && isMultiplex) || isRnaOnlyStudySetup,
        onClick: () =>
          openActionDialog(ActionDialogType.PostProcessingAndCalculateFeaturesSubMenu, {
            pipRunName: PipRunNameEnum.NeighborhoodClusters,
          }),
      },
      {
        type: ActionDialogType.RnaProbePeakDetection,
        header: 'Run Spot Detection',
        hide: !(canRunMultiplexPeakDetection && isMultiplex),
      },
      {
        type: ActionDialogType.MultiplexBlobSegmentation,
        header: 'Run Blob Segmentation',
        hide: !(canRunMultiplexBlobSegmentation && isMultiplex),
      },
      {
        isListSubheader: true,
        header: 'General Actions',
      },
      {
        type: ActionDialogType.ManualPostIngestPipeline,
        header: 'Run initial image analysis',
        hide: !(canRunManualPostIngestPipeline && studyIdIsSpecific),
        onClick: () => openActionDialog(ActionDialogType.ManualPostIngestPipeline),
      },
      {
        type: ActionDialogType.BulkSlideTagging,
        header: 'Bulk Slide Tagging',
        hide: !canEditSlideTagAssignments || !slidesMode,
      },
      {
        type: ActionDialogType.PostProcessingAndCalculateFeatures,
        header: 'Post Processing and Feature Calculation',
        hide: !canRunCalculateFeatures,
      },
      {
        type: ActionDialogType.NeighborhoodClusters,
        header: 'Run Neighborhood Clusters',
        hide: !canRunNeighborhoodClusters || isRnaOnlyStudySetup,
      },
      {
        type: ActionDialogType.AssignNeighborhoodNames,
        header: 'Assign Neighborhood Names',
        hide: !canAssignNeighborhoodNames || isRnaOnlyStudySetup,
      },
      {
        type: ActionDialogType.SlideRegistrations,
        header: 'Run Slide Registrations',
        hide: !canRunSlideRegistrations,
      },
      {
        type: ActionDialogType.CellQuantification,
        header: 'Run Stain Expression Quantification',
        hide: !canRunQuantification,
      },
      {
        type: ActionDialogType.DetectTmaCores,
        header: 'Detect TMA wells',
        hide: !canRunCropTma,
      },
      {
        type: ActionDialogType.CropTma,
        header: 'Run Crop TMA',
        hide: !canRunCropTma,
      },
      {
        type: ActionDialogType.CloudOptimizedSlides,
        header: 'Generate Cloud Optimized Slides',
        hide: !canExecuteCloudOptimizedSlides,
      },
      {
        type: ActionDialogType.TileGeneration,
        header: 'Run Tile Generation',
        hide: !canExecuteTileGeneration,
      },
      {
        isListSubheader: true,
        header: 'Analysis and Reports',
      },
      {
        type: ActionDialogType.QaAnalysis,
        header: 'Run QA Analysis',
        hide: !(canRunQaAnalysis && isMultiplex) || isRnaOnlyStudySetup,
      },
      {
        type: ActionDialogType.QcAnalysis,
        header: 'Run QC Analysis',
        hide: !(canRunQcAnalysis && isMultiplex),
      },
      {
        type: ActionDialogType.MifMarkerUnsupervisedAnalysis,
        header: 'Run mIF Marker Unsupervised Analysis',
        hide: !(canRunMifMarkerUnsupervisedAnalysis && isMultiplex) || isRnaOnlyStudySetup,
      },
      {
        type: ActionDialogType.StudyReport,
        header: 'Generate Study Report',
        hide: !canExecuteStudyReport,
      },
    ];
  }, [
    canApproveResults,
    canExecuteCloudOptimizedSlides,
    canExecuteTileGeneration,
    canExecuteStudyAlgorithms,
    canCreateAnnotationAssignment,
    canRunCalculateFeatures,
    canRunInference,
    canRunMultiplexBinaryClassifier,
    canRunMultiplexTissueSegmentationMask,
    canRunBrightfieldTissueSegmentationMask,
    canRunMultiplexCellSegmentation,
    canRunMultiplexCellTyping,
    canRunMultiplexHistogram,
    canRunMultiplexBlobSegmentation,
    canRunMultiplexNormalization,
    canRunMultiplexPeakDetection,
    canRunMultiplexThreshold,
    isBrightfield,
    hasStudyAlgorithms,
    isMultiplex,
    isRnaOnlyStudySetup,
    addToAssignmentSubItems,
    canApproveCases,
    canAssignNeighborhoodNames,
    canRunBrightfieldTissueSegmentationMask,
    canRunCropTma,
    canRunNeighborhoodClusters,
    canRunCellSegmentation,
    canRunQuantification,
    canRunQaAnalysis,
    canRunQcAnalysis,
    canRunMifMarkerUnsupervisedAnalysis,
    canExecuteStudyReport,
    canEditSlideTagAssignments,
    canEditQCMarkers,
    openPendingSlidesManifestDialog,
    pendingSlidesMode,
    slidesMode,
    studyIdIsSpecific,
    openInferenceDialog,
  ]);

  return {
    actionItems,
    isLoading: isLoadingStains || isLoadingStudyAlgorithms || isLoadingStudy,
    anchorEl,
    setAnchorEl,
    submenuAnchorEl,
    setSubmenuAnchorEl,
    open,
    handleClick,
    handleSubmenuOpen,
    handleSubmenuClose,
    handleMenuClose,
  };
};
